import React from 'react';
import { Users, Briefcase, GraduationCap, Network, DollarSign, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const benefits = [
  {
    icon: Users,
    title: "One-on-One Mentorship",
    description: "Get paired with experienced professionals who understand your journey and are committed to your success.",
    color: "text-cyan-500"
  },
  {
    icon: Briefcase,
    title: "Job Opportunities",
    description: "Access exclusive job openings and internship opportunities through our extensive network of partners.",
    color: "text-blue-500"
  },
  {
    icon: GraduationCap,
    title: "Professional Workshops",
    description: "Attend free workshops covering resume building, interview skills, networking, and industry insights.",
    color: "text-purple-500"
  },
  {
    icon: Network,
    title: "Networking Events",
    description: "Connect with industry leaders, fellow Cubs, and Lions at our regular networking events and meetups.",
    color: "text-cyan-500"
  },
  {
    icon: DollarSign,
    title: "Scholarships",
    description: "Apply for scholarships to help fund your education and professional development courses.",
    color: "text-green-500"
  },
  {
    icon: TrendingUp,
    title: "Career Development",
    description: "Receive guidance on career planning, goal setting, and strategies to advance in your chosen field.",
    color: "text-blue-500"
  }
];

const Benefits = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-blue-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Why Join Our Program?</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Discover the incredible benefits and opportunities available to all Cubs in our mentorship program.
          </p>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            Program Benefits
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <CardContent className="p-8">
                    <div className={`w-16 h-16 ${benefit.color} mb-4`}>
                      <Icon className="w-full h-full" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3 text-gray-900">{benefit.title}</h3>
                    <p className="text-gray-700">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Cubs Say */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            What Our Cubs Say
          </h2>
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 mb-6">
              <CardContent>
                <p className="text-xl italic text-gray-700 mb-4">
                  "The mentorship I received through Lions Unleashed was instrumental in landing my dream job. My mentor helped me refine my resume, practice interviews, and navigate the corporate world as a first-generation immigrant."
                </p>
                <p className="font-semibold text-gray-900">- Maria R., Software Engineer</p>
              </CardContent>
            </Card>
            <Card className="p-8 mb-6">
              <CardContent>
                <p className="text-xl italic text-gray-700 mb-4">
                  "I didn't have any professional connections when I started college. Lions Unleashed gave me access to a network of successful professionals who genuinely cared about my success."
                </p>
                <p className="font-semibold text-gray-900">- Ahmed H., Financial Analyst</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-blue-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Access These Benefits?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join our program today and start your journey to professional success. It's completely FREE!
          </p>
          <Link to="/register/cub">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 bg-white text-cyan-600 hover:bg-gray-100 font-bold"
            >
              Apply as a Cub
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Benefits;